// Resolve ONE lead object out of the several shapes the contact popup is
// opened with. Sibling of contactPracticeContext.js — same idea (normalise a
// value that arrives differently depending on the entry point), applied to the
// lead itself rather than the practice id.
//
// WHY THIS EXISTS
// ---------------
// ContactLeadPopupRouter does `popupLead = leadDetail || user` and hands that
// straight to FollowUpV2 as `leadDetail`. Four different shapes reach it:
//
//   1. FULL lead detail — GET /leads/{id}/ (OverView.jsx, LeadDetailTasks.jsx,
//      and Redux state.lead.followLead / state.lead.leadDetail).
//   2. LEADS-TABLE ROW — useLeadTableHelper.handleTableData. It FLATTENS and
//      RENAMES every nested field:
//          lead_contact    -> phone / email / note / preferred_contact_method
//          lead_source     -> referral / affiliate_user / metadata
//          lead_treatments -> treatmentData
//      ...and keeps the untouched API lead at `originalData`.
//      (NewLead.jsx, NHSLead.jsx)
//   3. TASK / REMINDER ROW — getRowLeadData.setRowLeadData or TaskReminder's
//      inline builder. Thinnest of all; `treatmentData` here is a SINGLE
//      reminder object, not an array, and `id` may be absent (only `lead_id`).
//      (TaskReminder.jsx, TaskBodyView.jsx)
//   4. WHATSAPP / DIALOG shapes — partial leads assembled per call site.
//
// FollowUpV2 read `leadDetail.lead_source.type`, `.lead_contact.preferred_*`,
// `.tags`, `.lead_contact.note` and `.contacted_count` DIRECTLY, and every one
// of those renders behind a truthiness gate. On shapes 2-4 they are undefined,
// so the source chip, the referral chip, the tags row, the "X prefers..." row
// and the ENQUIRY block silently disappeared — the same lead looked different
// depending on which button opened it.
//
// The 3-tier fallback this module generalises already existed in
// FollowUpV2.jsx (leadDetail -> id-matched followLead -> id-matched
// selfFetchedLead) but was wired to `lead_treatments` only. This module applies
// the same precedence to EVERY hero field, and adds a zero-latency tier-0:
// table rows already carry the full lead at `originalData`, so there is nothing
// to wait for on that path.
//
// PRECEDENCE, per field, first non-empty wins:
//   leadDetail -> leadDetail.originalData -> followLead -> selfFetchedLead
//   -> flattened row fields (synthesised back into lead_contact / lead_source)
// Every candidate after the first is id-guarded, so a stale followLead from a
// previously-open popup can never bleed into the lead on screen.

/** Empty-ish check: null/undefined/'' and empty arrays all count as "absent". */
const hasValue = (value) => {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
};

/**
 * The lead's numeric id, whichever shape it arrived in.
 *
 * Table rows key off `id`; task/reminder rows carry only `lead_id`. v2-flat
 * composite row ids are "<leadId>-<treatmentId>" (see the composite-id footgun
 * noted in FollowUpV2.jsx) — the lead id is the head segment, which is exactly
 * what handleTableData itself does to derive `lead_id`.
 *
 * Returns null when nothing usable is present, mirroring normalizePracticeId.
 */
export function resolveContactLeadId(lead) {
    const raw = lead?.id ?? lead?.lead_id ?? null;
    if (raw === null || raw === undefined || raw === '') return null;
    const head = String(raw).split('-')[0];
    const numeric = Number(head);
    return Number.isSafeInteger(numeric) && numeric > 0 ? numeric : null;
}

/** Treatments array, tolerating the table row's `treatmentData` alias. */
const readTreatments = (lead) => {
    if (Array.isArray(lead?.lead_treatments)) return lead.lead_treatments;
    // Task/reminder rows also use `treatmentData`, but for a SINGLE reminder
    // object — only accept the alias when it is genuinely the array shape.
    if (Array.isArray(lead?.treatmentData)) return lead.treatmentData;
    return undefined;
};

/**
 * Rebuild `lead_contact` from the flattened columns a table row carries.
 * Last-resort tier: it has no preferred_contact_days / _periods (the row drops
 * them), so it only ever fills in for a lead whose full object hasn't arrived.
 */
const synthesiseLeadContact = (lead) => {
    if (!lead) return undefined;
    const parts = {
        id: lead.lead_contact_id,
        phone: lead.phone,
        email: lead.email,
        note: lead.note,
        preferred_contact_method: lead.preferred_contact_method,
    };
    if (![parts.phone, parts.email, parts.note, parts.preferred_contact_method].some(hasValue)) {
        return undefined;
    }
    return { ...parts, preferred_contact_days: [], preferred_contact_periods: [] };
};

/**
 * Rebuild `lead_source` from the flattened columns a table row carries.
 * NOTE: the row drops `lead_source.type` entirely, so the source chip still
 * needs originalData / followLead. Referral survives and is restored here.
 */
const synthesiseLeadSource = (lead) => {
    if (!lead) return undefined;
    if (![lead.referral, lead.affiliate_user, lead.metadata].some(hasValue)) return undefined;
    return {
        referral: lead.referral,
        affiliate_user: lead.affiliate_user,
        metadata: lead.metadata,
    };
};

/** Fields read straight off a lead-detail-shaped object, in output order. */
const DIRECT_FIELDS = {
    first_name: (l) => l?.first_name,
    last_name: (l) => l?.last_name,
    salutation: (l) => l?.salutation,
    dob: (l) => l?.dob,
    status: (l) => l?.status,
    practice: (l) => l?.practice,
    lead_address: (l) => l?.lead_address,
    lead_contact: (l) => l?.lead_contact,
    lead_source: (l) => l?.lead_source,
    tags: (l) => l?.tags,
    lead_notes: (l) => l?.lead_notes,
    lead_metadata: (l) => l?.lead_metadata,
    lead_treatments: readTreatments,
    contacted_count: (l) => l?.contacted_count,
    assignees: (l) => l?.assignees,
    task_id: (l) => l?.task_id ?? l?.taskId,
};

/** Row-shape rescues, applied only after every real lead shape came up empty. */
const ROW_FALLBACKS = {
    lead_contact: synthesiseLeadContact,
    lead_source: synthesiseLeadSource,
};

/**
 * @param {object}  args
 * @param {object}  args.leadDetail       whatever the caller passed as the lead
 * @param {object} [args.followLead]      Redux state.lead.followLead
 * @param {object} [args.selfFetchedLead] FollowUpV2's tier-2 self-fetch result
 * @returns {object} a lead-detail-shaped object, plus:
 *   `id`        — the resolved numeric lead id (null when unresolvable)
 *   `__sources` — { field: 'leadDetail'|'originalData'|'followLead'|
 *                          'selfFetchedLead'|'rowFallback'|null }
 *                 Diagnostic only. Surfaced by ContactPopupDebugBox so a single
 *                 screenshot shows which entry point starved which field.
 */
export function resolveContactLead({ leadDetail, followLead, selfFetchedLead } = {}) {
    const anchorId = resolveContactLeadId(leadDetail);
    const isSameLead = (candidate) => {
        if (!candidate || anchorId === null) return false;
        return resolveContactLeadId(candidate) === anchorId;
    };

    // Tier 0 is free: handleTableData already stashed the untouched API lead.
    const embedded = leadDetail?.originalData;

    const candidates = [
        ['leadDetail', leadDetail],
        ['originalData', isSameLead(embedded) ? embedded : null],
        ['followLead', isSameLead(followLead) ? followLead : null],
        ['selfFetchedLead', isSameLead(selfFetchedLead) ? selfFetchedLead : null],
    ].filter(([, lead]) => !!lead);

    const resolved = { id: anchorId ?? leadDetail?.id ?? null };
    const sources = {};

    Object.entries(DIRECT_FIELDS).forEach(([field, read]) => {
        const hit = candidates.find(([, lead]) => hasValue(read(lead)));
        if (hit) {
            resolved[field] = read(hit[1]);
            sources[field] = hit[0];
            return;
        }
        const fallback = ROW_FALLBACKS[field]?.(leadDetail);
        if (hasValue(fallback)) {
            resolved[field] = fallback;
            sources[field] = 'rowFallback';
            return;
        }
        sources[field] = null;
    });

    resolved.__sources = sources;
    return resolved;
}

/**
 * Compact presence map for the superadmin debug box: one line per field saying
 * which tier supplied it, or 'missing'. Keeps the dump copy-pasteable.
 */
export function describeContactLeadSources(resolvedLead) {
    const sources = resolvedLead?.__sources || {};
    return Object.keys(sources).reduce((acc, field) => {
        acc[field] = sources[field] || 'missing';
        return acc;
    }, {});
}

export default resolveContactLead;
